import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { startStorefrontPreview } from '../designs/designs.api'
import {
  createCustomPage,
  deleteCustomPage,
  getCustomPage,
  getCustomPages,
  saveCustomPagePreviewDraft,
  updateCustomPage,
  type CustomPageOrganizationScope,
  type CustomPageShowKeyEntries,
} from './custom-pages.api'
import type {
  CustomPage,
  CustomPageIdInput,
  CustomPageInput,
  CustomPagePreviewDraftInput,
  CustomPageSummary,
} from './custom-pages.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

interface CustomPageScope extends OrganizationScope {
  customPageId: CustomPageIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type CustomPageIndexQueryKey = [AdminQueryRoot & CustomPageOrganizationScope]
type CustomPageShowQueryKey = [AdminQueryRoot & CustomPageShowKeyEntries]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

// `entity: 'customPage'` with `scope: 'show'` and `customPageId` lets breadcrumbs repaint the page
// title from the show query after a save (admin-route-breadcrumb-conventions.js).
export const customPageQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'customPage' }, { organizationId }),
  ],
  index: ({ organizationId }: OrganizationScope = {}): CustomPageIndexQueryKey =>
    buildScopedAdminQueryKey(customPageQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
  show: ({ organizationId, customPageId }: CustomPageScope): CustomPageShowQueryKey =>
    buildScopedAdminQueryKey(customPageQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      customPageId: Number(customPageId),
    }),
}

export const customPageQueries = {
  index: ({ organizationId }: OrganizationScope = {}) =>
    queryOptions({
      queryKey: customPageQueryKeys.index({ organizationId }),
      queryFn: getCustomPages,
    }),
  show: ({ organizationId, customPageId }: CustomPageScope) =>
    queryOptions({
      queryKey: customPageQueryKeys.show({ organizationId, customPageId }),
      queryFn: getCustomPage,
      // A save stores its response here, and its `warnings` (what the sanitizer removed) show until
      // the next save. A focus refetch would swap them for the stored page's, which are normally
      // none, while staff may still be reading them.
      refetchOnWindowFocus: false,
    }),
}

export function useCustomPages({
  organizationId,
  ...params
}: OrganizationScope & QueryParams<ReturnType<typeof customPageQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...customPageQueries.index({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useCustomPage(
  customPageId: CustomPageIdInput,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof customPageQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...customPageQueries.show({ organizationId: resolvedOrganizationId, customPageId }),
    ...params,
  })
}

/** Keeps the saved page (with its save warnings) for its editor and refreshes the list. */
function syncSavedCustomPage(organizationId: number, customPage: CustomPage) {
  queryClient.setQueryData(
    customPageQueryKeys.show({ organizationId, customPageId: customPage.id }),
    customPage
  )

  return queryClient.invalidateQueries({
    queryKey: customPageQueryKeys.index({ organizationId }),
  })
}

/**
 * Shows unsaved HTML in a storefront preview session of its own, so the page's main preview session
 * always shows the saved page and nothing has to be cleared after a save or reset. Resolves to that
 * session's token, which later drafts reuse. Drafts for one page are sent in order.
 */
export function useCustomPagePreviewDraftMutation(customPageId: CustomPageIdInput) {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    scope: { id: `custom-page-preview-draft-${customPageId}` },
    mutationFn: async ({
      previewBaseUrl,
      draftToken,
      html,
    }: {
      previewBaseUrl: string
      draftToken: string | null
      html: string
    }) => {
      const previewToken =
        draftToken ?? (await startStorefrontPreview({ organizationId, previewBaseUrl }))
      const values: CustomPagePreviewDraftInput = { preview_token: previewToken, html }

      await saveCustomPagePreviewDraft({ organizationId, customPageId, values })

      return previewToken
    },
  })
}

/** Creates a page, or replaces every field of `customPageId` when given. */
export function useSaveCustomPageMutation(customPageId?: CustomPageIdInput) {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CustomPageInput) =>
      customPageId === undefined
        ? createCustomPage({ organizationId, values })
        : updateCustomPage({ organizationId, customPageId, values }),
    onSuccess: (customPage) => syncSavedCustomPage(organizationId, customPage),
  })
}

/**
 * The page's editor still watches its show query when the delete succeeds, and removing a watched
 * query refetches it (a 404). Callers navigate away first, then call `cleanupDeletedCustomPage`.
 */
export function useDeleteCustomPageMutation() {
  const organizationId = useRouteOrganizationId()

  const mutation = useMutation({
    mutationFn: (customPageId: CustomPageIdInput) =>
      deleteCustomPage({ organizationId, customPageId }),
    onSuccess: (_, customPageId) => {
      queryClient.setQueryData<CustomPageSummary[]>(
        customPageQueryKeys.index({ organizationId }),
        (customPages) => customPages?.filter((page) => page.id !== Number(customPageId))
      )

      return queryClient.invalidateQueries({
        queryKey: customPageQueryKeys.index({ organizationId }),
      })
    },
  })

  const cleanupDeletedCustomPage = (customPageId: CustomPageIdInput) => {
    queryClient.removeQueries({
      queryKey: customPageQueryKeys.show({ organizationId, customPageId }),
    })
  }

  return { ...mutation, cleanupDeletedCustomPage }
}
